import type { ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";

import { IconButton } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { NT_PURCHASE_PAGE_LINK } from "app/pages/sales-intelligence/constants/common";
import ContactsExportCommonItems from "app/pages/sales-intelligence/sub-modules/contacts-search/components/ContactsExportDropdown/ContactsExportCommonItems";
import {
    selectHasRevealedContacts,
    selectIsContactsPendingApproval,
    selectIsContactsReadonly,
    selectIsContactsVisible,
} from "app/pages/sales-intelligence/sub-modules/contacts-tab/store/selectors";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";

import ContactsExportDropdown from "../../components/ContactsExportDropdown/ContactsExportDropdown";
import trackingService from "../../services/tracking/trackingService";
import { initExportToCrm, initExportToExcel } from "../../store/action-creators";
import { selectIsExportFlowActive } from "../../store/selectors";
import type { CRMEntity } from "../../types/common";

type ExportVariantsDropdownContainerBaseProps = {
    isLoading: boolean;
    numberOfResults: number;
    onActivate: VoidFunction;
    onClick?: VoidFunction;
};

const ContactsExportDropdownContainer = ({
    isLoading,
    numberOfResults,
    onActivate,
    onClick,
}: ExportVariantsDropdownContainerBaseProps): ReactElement => {
    const dispatch = useDispatch();

    const isContactsVisible = useSelector(selectIsContactsVisible);
    const hasRevealedContacts = useSelector(selectHasRevealedContacts);
    const isContactsReadonly = useSelector(selectIsContactsReadonly);
    const isContactsPendingApproval = useSelector(selectIsContactsPendingApproval);
    const isExportOngoing = useSelector(selectIsExportFlowActive);
    const isReadonlyAccessWithoutRevealedContacts = isContactsReadonly && !hasRevealedContacts;
    const isDisabled = !isContactsVisible || numberOfResults <= 0 || isExportOngoing;

    function handleExcelExportSelect() {
        trackingService.trackSelectExportDestination("excel");
        dispatch(initExportToExcel());
        onActivate();
    }

    function handleCRMExportSelect(destination: Integration, as: CRMEntity = "contact") {
        trackingService.trackSelectExportDestination(destination);
        dispatch(initExportToCrm({ crm: destination, as }));
        onActivate();
    }

    const handleHookExportOptionsClicked = () => {
        trackingService.trackTrialExportOptionsClicked(NT_PURCHASE_PAGE_LINK);
        window.open(NT_PURCHASE_PAGE_LINK);
    };

    if (isContactsReadonly || isContactsPendingApproval) {
        return (
            <IconButton
                iconName="sparkles"
                type="flatTrial"
                onClick={handleHookExportOptionsClicked}
            >
                {translate("si.advanced_search.contacts_search.action.export.text")}
            </IconButton>
        );
    }

    return (
        <ContactsExportDropdown
            isDisabled={isLoading || isDisabled}
            buttonIconName="share"
            buttonText={translate("si.advanced_search.contacts_search.action.export.text")}
            dataAutomation="export-variant-dropdown.button"
            onClick={onClick}
        >
            <ContactsExportCommonItems
                isExportDisabled={isReadonlyAccessWithoutRevealedContacts}
                disabledExportTooltip={translate("si.contacts-export.disabled.text")}
                onExcelExport={handleExcelExportSelect}
                onCRMExport={handleCRMExportSelect}
            />
        </ContactsExportDropdown>
    );
};

export default ContactsExportDropdownContainer;
